import React, { useEffect, useRef, useState } from "react";
import { Car, Truck, Container, Check, AlertTriangle, XOctagon, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { INSPECTION, labelOf } from "@/lib/inspection";
import { MechanicService } from "./MechanicService";
import { SendReportButton } from "@/components/SendReportButton";

const TYPES = [["car", Car], ["truck", Truck], ["trailer", Container]];
const ST = {
  ok: { icon: Check, cls: "bg-emerald-500 text-white border-emerald-500" },
  attention: { icon: AlertTriangle, cls: "bg-amber-500 text-white border-amber-500" },
  replace: { icon: XOctagon, cls: "bg-rose-500 text-white border-rose-500" },
};

export const MechanicInspection = ({ card, lang, t, locked, onSave, onSent }) => {
  const initType = card.inspection?.vehicle_type || card.vehicle_type || "car";
  const [vtype, setVtype] = useState(initType);
  const [items, setItems] = useState(card.inspection?.items || {});
  const [noteFor, setNoteFor] = useState(null);
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => { if (!dirty.current) { setItems(card.inspection?.items || {}); setVtype(card.inspection?.vehicle_type || card.vehicle_type || "car"); } }, [card.inspection, card.vehicle_type]);

  const queue = (nextItems, nextType) => {
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => { await onSave({ vehicle_type: nextType, items: nextItems }); dirty.current = false; }, 600);
  };
  const setStatus = (key, label, status) => {
    if (locked) return;
    const cur = items[key];
    const next = { ...items };
    if (cur?.status === status) delete next[key]; else next[key] = { ...(cur || {}), status, label };
    setItems(next); queue(next, vtype);
  };
  const setNote = (key, label, note) => { const next = { ...items, [key]: { ...(items[key] || { status: "attention" }), label, note } }; setItems(next); queue(next, vtype); };
  const changeType = (tp) => { setVtype(tp); queue(items, tp); };

  const sections = INSPECTION[vtype] || INSPECTION.car;
  const inspItems = Object.fromEntries(Object.entries(items).filter(([k]) => !k.startsWith("svc_")));
  const flagged = Object.values(inspItems).filter((v) => v.status !== "ok").length;
  const checked = Object.keys(inspItems).length;
  const totalItems = sections.reduce((s, sec) => s + sec.items.length, 0);
  const onServiceChange = (next) => { setItems(next); queue(next, vtype); };

  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4" data-testid="mechanic-step-inspection">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-base md:text-lg flex items-center gap-2"><span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-mono font-bold text-sm">3</span>{t("mech_inspection_title")}</h2>
        <div className="inline-flex rounded-full border border-border p-0.5 bg-background" data-testid="mechanic-vehicle-type">
          {TYPES.map(([tp, Icon]) => (
            <button key={tp} disabled={locked} onClick={() => changeType(tp)} className={`px-3 h-9 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${vtype === tp ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`mechanic-vtype-${tp}`}><Icon className="h-4 w-4" />{t(`mech_type_${tp}`)}</button>
          ))}
        </div>
      </div>
      <MechanicService vtype={vtype} items={items} lang={lang} t={t} locked={locked} onChange={onServiceChange} />
      <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground flex-wrap" data-testid="mechanic-inspection-progress">
        <span>{checked}/{totalItems}</span>
        {flagged > 0 && <span className="text-amber-600 font-semibold">{flagged} {t("mech_flagged")}</span>}
        <div className="h-1.5 flex-1 min-w-[80px] rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${Math.round((checked / Math.max(totalItems, 1)) * 100)}%` }} /></div>
      </div>
      {checked > 0 && <SendReportButton card={card} t={t} locked={locked} onSent={onSent} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {sections.map((sec) => (
          <div key={sec.key} className="rounded-xl border border-border overflow-hidden" data-testid={`mechanic-insp-section-${sec.key}`}>
            <div className="px-3 py-2 bg-muted/50 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{labelOf(sec.label, lang)}</div>
            <div className="divide-y divide-border">
              {sec.items.map(([key, lbl]) => {
                const label = labelOf(lbl, lang); const cur = items[key];
                return (
                  <div key={key} className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm flex-1 truncate ${cur ? "font-medium" : ""}`}>{label}</span>
                      {Object.entries(ST).map(([s, { icon: Icon, cls }]) => (
                        <button key={s} disabled={locked} onClick={() => setStatus(key, label, s)} title={t(`mech_${s}`)} className={`h-10 w-10 rounded-lg border grid place-items-center transition-colors ${cur?.status === s ? cls : "border-border text-muted-foreground hover:border-primary"}`} data-testid={`mechanic-insp-${key}-${s}`}><Icon className="h-4 w-4" /></button>
                      ))}
                      <button disabled={locked} onClick={() => setNoteFor(noteFor === key ? null : key)} className={`h-10 w-10 rounded-lg border grid place-items-center ${cur?.note ? "border-primary text-primary" : "border-border text-muted-foreground"}`} data-testid={`mechanic-insp-${key}-note`}><MessageSquare className="h-4 w-4" /></button>
                    </div>
                    {(noteFor === key || cur?.note) && (
                      <Input value={cur?.note || ""} onChange={(e) => setNote(key, label, e.target.value)} placeholder={t("mech_note_ph")} disabled={locked} className="h-10 mt-2 text-sm" data-testid={`mechanic-insp-${key}-note-input`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
